import { Paper, Typography, Box, CircularProgress } from "@mui/material";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { themeColors } from "../styles/theme";

// Updated interface to support multiple gardens
interface SunDataPoint {
  date: string;
  [gardenId: string]: number | string; // Allow for garden-specific values plus the date
}

interface YearlySunChartProps {
  data: SunDataPoint[];
  gardenDetails: { 
    [gardenId: string]: { 
      name: string;
      color: string;
    } 
  };
  isLoading: boolean;
  error: string | null;
}

// Define colors directly in this component
const colors = {
  orange: themeColors.orange,
  darkBackground: themeColors.darkBackground,
  blue: themeColors.blue
};

// Array of distinct colors for multiple gardens
const GARDEN_COLORS = [
  colors.orange,
  colors.blue,
  '#4CAF50', // Green
  '#9C27B0', // Purple
  '#FF5722', // Deep Orange
  '#2196F3', // Blue
  '#795548', // Brown
  '#607D8B', // Blue Grey
];

const YearlySunChart = ({ data, gardenDetails, isLoading, error }: YearlySunChartProps) => {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { month: 'short' });
  };

  // Get all garden IDs from the gardenDetails
  const gardenIds = Object.keys(gardenDetails);

  return (
    <Paper
        elevation={3}
        sx={{
          borderRadius: 2,
          p: 3,
          mb: 3,
          background: themeColors.paperYellow,
          backdropFilter: 'blur(16px)',
        }}
    >
      <Typography
        variant="h5" 
        sx={{ 
          mb: 1, 
          fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          color: colors.darkBackground
        }}
      >
        {'Yearly Sunshine'}
      </Typography>
        
      <Box sx={{ height: '220px', width: '100%' }}> {/* Increased height to accommodate legend */}
        {isLoading ? (
          <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <CircularProgress size={40} sx={{ color: colors.blue }} />
          </Box>
        ) : error ? (
          <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              {error}
            </Typography>
          </Box>
        ) : data.length < 5 ? (
          <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              This pub was only recently added. We're still working on this one — please check back later! 🍺
            </Typography>
          </Box>
        ) : data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 5, right: 10, left: 0, bottom: 25 }} // Increased bottom margin for legend
            >
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                tick={{ fontSize: 10, fill: '#333333' }}
                tickMargin={5}
                axisLine={{ stroke: '#f0f0f0' }}
                tickLine={false}
              />
              <YAxis 
                width={35}
                tick={{ fontSize: 10, fill: '#333333' }}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={(value) => `${value}%`}
                domain={[0, 100]}
                axisLine={false}
                tickLine={false}
                stroke="#333333"
              />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  // Find the garden name from garden ID
                  const gardenName = gardenIds.includes(name) 
                    ? gardenDetails[name].name
                    : name;
                  
                  return [`${value.toFixed(1)}% of garden in sun`, gardenName];
                }}
                labelFormatter={(label) => new Date(label).toLocaleDateString('en-GB', { 
                  month: 'long', 
                  day: 'numeric' 
                })}
                contentStyle={{ 
                  borderRadius: '8px',
                  fontSize: '0.8rem',
                  border: 'none',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  color: '#333333'
                }}
                itemStyle={{ color: '#333333' }}
                labelStyle={{ color: '#333333', fontWeight: 600 }}
              />
              <Legend 
                verticalAlign="bottom"
                height={36}
                formatter={(value, entry) => {
                  // Find the garden name from the garden ID
                  return gardenIds.includes(value)
                    ? gardenDetails[value].name
                    : value;
                }}
              />
              
              {/* Filter out the 'average' garden and render a line for each garden */}
              {gardenIds.filter(gardenId => gardenId !== 'average').map((gardenId, index) => (
                <Line 
                  key={gardenId}
                  type="monotone" 
                  dataKey={gardenId}
                  name={gardenId} // Will be formatted by legend formatter
                  stroke={gardenDetails[gardenId].color || GARDEN_COLORS[index % GARDEN_COLORS.length]}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ 
                    r: 4, 
                    stroke: gardenDetails[gardenId].color || GARDEN_COLORS[index % GARDEN_COLORS.length], 
                    strokeWidth: 1 
                  }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="body2">
              No yearly sun data available yet.
            </Typography>
          </Box>
        )}
      </Box>
        
      <Typography variant="body2" sx={{ mt: 1, color: '#333333', textAlign: 'center', fontSize: '0.8rem' }}>
        Percentage of garden area in direct sunlight throughout the year
      </Typography>
    </Paper>

  );
};

export default YearlySunChart;